// docs/62 — the emote wheel. Hold T (EM3) or press the emote button and a wheel of the 32 emotes
// opens in the middle of the screen: 8 on the inner ring (keys 1–8), 24 on the outer ring (the
// letter keys, shown on each slot). Every key here is a hotkey the player may rebind (docs/87). While it is open a key fires its emote, pointing at a slot
// and letting go of T (or clicking) fires that one, Tab picks which of my heroes on screen emotes,
// and Esc or a click in the middle closes it. Every other key is held back from the screen under
// it, so Q, E and WASD don't zoom or pan the board while choosing. On a ranger's companion the
// wheel is the pet's own (docs/59): four emotes on the inner ring's first four keys, no bubbles.
// A bard's wheel (docs/104 BD6) has a row of their own musical emotes under the rings; no other
// hero is offered them (and the module refuses them).
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { BARD_EMOTES, EMOTES, INNER_COUNT, PET_EMOTES, RING_EMOTES, WHEEL_HOTKEY, emoteForKey, emotesFor, petEmoteForKey } from '../../core/emote/catalogue';
import { hotkeyFor, isHotkey, isHotkeyRelease, keyLabel, useHotkeyOverrides, useKeyLabel } from '../../core/input/hotkeys';
import { isBardHero, isPet, myHeroesOnScreen, sendEmote, useEmotes, type EmoteHero } from '../../core/emote/store';
import { useAuthoring } from '../../core/stores/authoring';
import { useRoute } from '../../core/stores/route';
import { useSession } from '../../core/stores/session';
import { useDialogs } from '../components';
import { useOverlay } from '../overlay';
import { EmoteSprite } from './EmoteBubble';
import './emote.css';

/** Ring radii (CSS px, slot centres). */
export const R_INNER = 92;
export const R_OUTER = 178;
/** docs/104 BD6: the bard's row, below the wheel (slot centres), and its slots' spacing. */
export const BARD_ROW_Y = 262;
export const BARD_STEP = 56;
const DEAD_ZONE = 46;
const OUTER_COUNT = RING_EMOTES.length - INNER_COUNT;

const typing = (e: KeyboardEvent) => { const t = e.target as HTMLElement | null; return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable); };

/** Where a bard's slot `i` (0-based in `BARD_EMOTES`) sits: a row under the wheel, centred. */
function bardSlotAt(i: number): { x: number; y: number } {
  return { x: Math.round((i - (BARD_EMOTES.length - 1) / 2) * BARD_STEP), y: BARD_ROW_Y };
}

/** Where slot `id` sits, from the wheel's centre (the first of each ring at the top, clockwise; a
 *  bard's own in the row under it). */
export function slotAt(id: number): { x: number; y: number } {
  const b = BARD_EMOTES.findIndex((e) => e.id === id);
  if (b >= 0) return bardSlotAt(b);
  const inner = id < INNER_COUNT;
  const n = inner ? INNER_COUNT : OUTER_COUNT;
  const i = inner ? id : id - INNER_COUNT;
  const a = (i / n) * Math.PI * 2 - Math.PI / 2;
  const r = inner ? R_INNER : R_OUTER;
  return { x: Math.round(Math.cos(a) * r), y: Math.round(Math.sin(a) * r) };
}

/** The slot a pointer `dx, dy` from the centre points at: by ring (distance) and angle; for a bard,
 *  the row under the wheel first. */
export function slotFor(dx: number, dy: number, bard = false): number | undefined {
  if (bard && Math.abs(dy - BARD_ROW_Y) <= 28) {
    const i = Math.round(dx / BARD_STEP + (BARD_EMOTES.length - 1) / 2);
    if (i >= 0 && i < BARD_EMOTES.length && Math.abs(dx - bardSlotAt(i).x) <= BARD_STEP / 2) return BARD_EMOTES[i]!.id;
    return undefined;
  }
  const d = Math.hypot(dx, dy);
  if (d < DEAD_ZONE || d > R_OUTER + 60) return undefined;
  const inner = d < (R_INNER + R_OUTER) / 2;
  const n = inner ? INNER_COUNT : OUTER_COUNT;
  const turn = (Math.atan2(dy, dx) + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);
  const i = Math.round((turn / (Math.PI * 2)) * n) % n;
  return inner ? i : INNER_COUNT + i;
}

/** Where pet slot `id` sits: four on the inner ring, the first at the top, clockwise. */
export function petSlotAt(id: number): { x: number; y: number } {
  const a = (id / PET_EMOTES.length) * Math.PI * 2 - Math.PI / 2;
  return { x: Math.round(Math.cos(a) * R_INNER), y: Math.round(Math.sin(a) * R_INNER) };
}

/** The pet slot a pointer `dx, dy` from the centre points at: by angle, past the hub. */
export function petSlotFor(dx: number, dy: number): number | undefined {
  const d = Math.hypot(dx, dy);
  if (d < DEAD_ZONE || d > R_INNER + 90) return undefined;
  const n = PET_EMOTES.length;
  const turn = (Math.atan2(dy, dx) + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);
  return Math.round((turn / (Math.PI * 2)) * n) % n;
}

/** Can the wheel open now: signed in, on a game screen, no menu or dialog up, a hero of mine shown. */
function canOpen(): boolean {
  if (useSession.getState().phase !== 'ready') return false;
  if (useRoute.getState().screen === 'title') return false;
  const o = useOverlay.getState();
  if (o.menu || o.settings || o.levelUp !== undefined || o.credits || useDialogs.getState().queue.length) return false;
  return myHeroesOnScreen().length > 0;
}

/** The hero the wheel starts on: the one selected on the board, else the last one used, else the first. */
function startingHero(mine: EmoteHero[]): string | undefined {
  const ids = mine.map((h) => h.id.toString());
  const sel = useRoute.getState().screen === 'encounter' ? useAuthoring.getState().selectedHero : undefined;
  if (sel !== undefined && ids.includes(String(sel))) return String(sel);
  const last = useEmotes.getState().lastHero;
  if (last && ids.includes(last)) return last;
  return ids[0];
}

export function openWheel(mode: 'hold' | 'click'): boolean {
  if (!canOpen()) return false;
  const heroId = startingHero(myHeroesOnScreen());
  if (!heroId) return false;
  useEmotes.setState({ wheel: { mode, heroId } });
  return true;
}

export const closeWheel = () => useEmotes.setState({ wheel: undefined });

function cycleHero(step: number): void {
  const w = useEmotes.getState().wheel;
  const mine = myHeroesOnScreen();
  if (!w || mine.length < 2) return;
  const i = mine.findIndex((h) => h.id.toString() === w.heroId);
  const next = mine[(i + step + mine.length) % mine.length]!;
  useEmotes.setState({ wheel: { ...w, heroId: next.id.toString() } });
}

function fire(id: number): void {
  const w = useEmotes.getState().wheel;
  if (!w) return;
  closeWheel();
  sendEmote(w.heroId, id);
}

const WHEEL_COMMANDS = ['wheel.close', 'wheel.prevHero', 'wheel.nextHero'] as const;

/** The keys: T held opens, a slot's key fires, Tab cycles, Esc closes; capture phase, ahead of the screens. */
function useWheelKeys(hovered: React.MutableRefObject<number | undefined>): void {
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      const w = useEmotes.getState().wheel;
      if (!w) {
        if (e.repeat || !isHotkey(e, WHEEL_HOTKEY) || typing(e)) return;
        if (!openWheel('hold')) return;
        hovered.current = undefined;
        e.preventDefault(); e.stopPropagation();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault(); e.stopPropagation();
      const cmd = hotkeyFor(e, WHEEL_COMMANDS);
      if (cmd === 'wheel.close') { closeWheel(); return; }
      if (cmd === 'wheel.nextHero' || cmd === 'wheel.prevHero') { cycleHero(cmd === 'wheel.prevHero' ? -1 : 1); return; }
      if (e.repeat || isHotkeyRelease(e, WHEEL_HOTKEY)) return;
      const em = isPet(w.heroId) ? petEmoteForKey(e) : emoteForKey(e, undefined, isBardHero(w.heroId));
      if (em) fire(em.id);
    };
    const onUp = (e: KeyboardEvent) => {
      const w = useEmotes.getState().wheel;
      if (w?.mode !== 'hold' || !isHotkeyRelease(e, WHEEL_HOTKEY)) return;
      e.preventDefault(); e.stopPropagation();
      if (hovered.current !== undefined) fire(hovered.current); else closeWheel();
    };
    const onBlur = () => { if (useEmotes.getState().wheel?.mode === 'hold') closeWheel(); };
    window.addEventListener('keydown', onDown, true);
    window.addEventListener('keyup', onUp, true);
    window.addEventListener('blur', onBlur);
    return () => { window.removeEventListener('keydown', onDown, true); window.removeEventListener('keyup', onUp, true); window.removeEventListener('blur', onBlur); };
  }, [hovered]);
}

/** The wheel itself (pure: the gallery shows it with fixed props). */
export function WheelFace({ hero, heroes, hovered, bard = false, onHover, onPick, onCycle, onClose }: {
  hero?: EmoteHero; heroes: number; hovered?: number; bard?: boolean;
  onHover?: (id: number | undefined) => void; onPick?: (id: number) => void; onCycle?: (step: number) => void; onClose?: () => void;
}) {
  const shown = hovered !== undefined ? EMOTES[hovered] : undefined;
  const keys = useHotkeyOverrides();
  const nextKey = keyLabel('wheel.nextHero', keys);
  return (
    <div className="fk-wheel" data-testid="emote-wheel" data-bard={bard ? 'true' : undefined} role="menu" aria-label={`Emotes${hero ? ` for ${hero.name}` : ''}`}>
      <span className="fk-wheel__ring fk-wheel__ring--outer" aria-hidden="true" />
      <span className="fk-wheel__ring fk-wheel__ring--inner" aria-hidden="true" />
      {bard && <span className="fk-wheel__bard-row" aria-hidden="true" style={{ '--y': `${BARD_ROW_Y}px`, '--w': `${BARD_EMOTES.length * BARD_STEP + 12}px` } as CSSProperties}><span className="fk-wheel__bard-label">Bard's tunes</span></span>}
      {emotesFor(bard).map((e) => {
        const at = slotAt(e.id);
        const k = keyLabel(e.hotkey, keys);
        return (
          <button key={e.key} type="button" role="menuitem" className={`fk-wheel__slot fk-wheel__slot--${e.ring === 'bard' ? 'outer fk-wheel__slot--bard' : e.ring} ${hovered === e.id ? 'is-hot' : ''}`}
            style={{ '--x': `${at.x}px`, '--y': `${at.y}px` } as CSSProperties} data-testid="emote-slot" data-emote={e.key}
            title={k ? `${e.name} (${k})` : e.name} aria-label={k ? `${e.name}, key ${k}` : e.name}
            onPointerEnter={() => onHover?.(e.id)} onClick={(ev) => { ev.stopPropagation(); onPick?.(e.id); }}>
            <EmoteSprite emote={e.id} scale={e.ring === 'inner' ? 2 : 1} />
            {k && <kbd className="fk-wheel__key">{k}</kbd>}
          </button>
        );
      })}
      <div className="fk-wheel__hub" onClick={(ev) => { ev.stopPropagation(); onClose?.(); }}>
        <span className="fk-wheel__who">
          {heroes > 1 && <button type="button" className="fk-wheel__cycle" aria-label="Previous hero" onClick={(ev) => { ev.stopPropagation(); onCycle?.(-1); }}>‹</button>}
          <span className="fk-wheel__name" data-testid="emote-wheel-hero">{hero?.name ?? '—'}</span>
          {heroes > 1 && <button type="button" className="fk-wheel__cycle" aria-label="Next hero" onClick={(ev) => { ev.stopPropagation(); onCycle?.(1); }}>›</button>}
        </span>
        <span className="fk-wheel__what">{shown ? shown.name : heroes > 1 && nextKey ? `${nextKey}: next hero` : 'pick an emote'}</span>
      </div>
    </div>
  );
}

/** A companion's wheel (docs/59): its four pet emotes, no bubbles; the hub as the hero wheel's. */
export function PetWheelFace({ hero, heroes, hovered, onHover, onPick, onCycle, onClose }: {
  hero?: EmoteHero; heroes: number; hovered?: number;
  onHover?: (id: number | undefined) => void; onPick?: (id: number) => void; onCycle?: (step: number) => void; onClose?: () => void;
}) {
  const shown = hovered !== undefined ? PET_EMOTES[hovered] : undefined;
  const keys = useHotkeyOverrides();
  const nextKey = keyLabel('wheel.nextHero', keys);
  return (
    <div className="fk-wheel fk-wheel--pet" data-testid="emote-wheel" data-pet="true" role="menu" aria-label={`Pet emotes${hero ? ` for ${hero.name}` : ''}`}>
      <span className="fk-wheel__ring fk-wheel__ring--inner" aria-hidden="true" />
      {PET_EMOTES.map((e) => {
        const at = petSlotAt(e.id);
        const k = keyLabel(e.hotkey, keys);
        return (
          <button key={e.key} type="button" role="menuitem" className={`fk-wheel__slot fk-wheel__slot--inner fk-wheel__slot--pet ${hovered === e.id ? 'is-hot' : ''}`}
            style={{ '--x': `${at.x}px`, '--y': `${at.y}px` } as CSSProperties} data-testid="pet-emote-slot" data-emote={e.key}
            title={k ? `${e.name} (${k})` : e.name} aria-label={k ? `${e.name}, key ${k}` : e.name}
            onPointerEnter={() => onHover?.(e.id)} onClick={(ev) => { ev.stopPropagation(); onPick?.(e.id); }}>
            <span className="fk-wheel__pet-glyph" aria-hidden="true">{e.glyph}</span>
            <span className="fk-wheel__pet-name">{e.name}</span>
            {k && <kbd className="fk-wheel__key">{k}</kbd>}
          </button>
        );
      })}
      <div className="fk-wheel__hub" onClick={(ev) => { ev.stopPropagation(); onClose?.(); }}>
        <span className="fk-wheel__who">
          {heroes > 1 && <button type="button" className="fk-wheel__cycle" aria-label="Previous hero" onClick={(ev) => { ev.stopPropagation(); onCycle?.(-1); }}>‹</button>}
          <span className="fk-wheel__name" data-testid="emote-wheel-hero">{hero?.name ?? '—'}</span>
          {heroes > 1 && <button type="button" className="fk-wheel__cycle" aria-label="Next hero" onClick={(ev) => { ev.stopPropagation(); onCycle?.(1); }}>›</button>}
        </span>
        <span className="fk-wheel__what">{shown ? shown.name : heroes > 1 && nextKey ? `${nextKey}: next hero` : 'pick a mood'}</span>
      </div>
    </div>
  );
}

/** Mounted once in App: the hotkeys, the wheel while open, and the emote button. */
export function EmoteHost() {
  const wheel = useEmotes((s) => s.wheel);
  const screen = useRoute((s) => s.screen);
  const mine = useEmotes(useShallow((s) => myHeroesOnScreen(s)));
  const hoveredRef = useRef<number | undefined>(undefined);
  const [hovered, setHovered] = useState<number | undefined>();
  const centre = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  useWheelKeys(hoveredRef);
  const wheelKey = useKeyLabel(WHEEL_HOTKEY);
  const hover = (id: number | undefined) => { hoveredRef.current = id; setHovered(id); };
  useEffect(() => { if (!wheel) hover(undefined); }, [wheel]);
  // the hero left the screen (the fight ended, the band walked off): close
  const hero = wheel ? mine.find((h) => h.id.toString() === wheel.heroId) : undefined;
  useEffect(() => { if (wheel && !hero) closeWheel(); }, [wheel, hero]);
  const pet = !!wheel && isPet(wheel.heroId);
  const bard = !!wheel && !pet && isBardHero(wheel.heroId);
  const pick = (dx: number, dy: number) => (pet ? petSlotFor(dx, dy) : slotFor(dx, dy, bard));
  // a different wheel under the pointer: forget what the last one had hot
  useEffect(() => { hover(undefined); }, [pet]);

  return (
    <>
      {mine.length > 0 && !wheel && (
        <button type="button" className="fk-hit fk-emote-button" data-testid="emote-button" data-screen={screen} title={wheelKey ? `Emotes (hold ${wheelKey})` : 'Emotes'} aria-label={wheelKey ? `Emotes (hold ${wheelKey})` : 'Emotes'}
          onClick={() => openWheel('click')}>
          <EmoteSprite emote={3} scale={1} />
          {wheelKey && <kbd className="fk-emote-button__key">{wheelKey}</kbd>}
        </button>
      )}
      {wheel && hero && (
        <div className="fk-hit fk-wheel-veil" data-mode={wheel.mode}
          ref={(el) => { if (el) { const r = el.getBoundingClientRect(); centre.current = { x: r.left + r.width / 2, y: r.top + r.height / 2 }; } }}
          onPointerMove={(e) => hover(pick(e.clientX - centre.current.x, e.clientY - centre.current.y))}
          onClick={(e) => { const id = pick(e.clientX - centre.current.x, e.clientY - centre.current.y); if (id !== undefined) fire(id); else closeWheel(); }}
          onContextMenu={(e) => { e.preventDefault(); closeWheel(); }}>
          {pet
            ? <PetWheelFace hero={hero} heroes={mine.length} hovered={hovered} onHover={hover} onPick={fire} onCycle={cycleHero} onClose={closeWheel} />
            : <WheelFace hero={hero} heroes={mine.length} hovered={hovered} bard={bard} onHover={hover} onPick={fire} onCycle={cycleHero} onClose={closeWheel} />}
        </div>
      )}
    </>
  );
}
